import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';

const ManifestPage = () => {
  const [courierId, setCourierId] = useState('');
  const [shipmentMethods, setShipmentMethods] = useState([]);
  const [shipmentMethod, setShipmentMethod] = useState('');
  const [couriers, setCouriers] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    fetchCouriers();
    fetchShipmentMethods();
  }, []);

  // Fetch couriers
  const fetchCouriers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/courier/all`);
      if (!response.ok) throw new Error('Failed to fetch courier methods');
      const data = await response.json();
      if (Array.isArray(data)) {
        setCouriers(data);
        setCourierId(data[0]?.id);
      } else {
        throw new Error('Courier data is not in the expected format');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch shipment methods
  const fetchShipmentMethods = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/shipmentMethod`);
      if (!response.ok) throw new Error('Failed to fetch shipment methods');
      const data = await response.json();
      if (Array.isArray(data) && data.every(item => item.id && item.name)) {
        setShipmentMethods(data);
        setShipmentMethod(data[0]?.id);
      } else {
        throw new Error('Shipment method data is not in the expected format');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Generate manifest
  const generateManifest = async () => {
    if (!courierId || !shipmentMethod || !startDate || !endDate) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/generateManifest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startDate, endDate, courierId, shipmentMethod }),
      });
      if (!response.ok) throw new Error('Failed to generate manifest');
      const data = await response.json();
      setManifest(data);
    } catch (err) {
      setError(err.message || 'Failed to generate manifest');
      setManifest(null);

    } finally {
      setLoading(false);
    }
  };

  // Download CSV helper
  const downloadCSV = () => {
    if (!manifest?.packages?.length) return;
    const columns = [
      { header: 'Tracking Number', key: 'tracking_number' },
      { header: 'First Name', key: 'account.first_name' },
      { header: 'Last Name', key: 'account.last_name' },
      { header: 'Weight', key: 'weight' },
      { header: 'Description', key: 'description' },
      { header: 'House Number', key: 'airWayBill' },
      { header: 'Courier', key: 'shipper.username' },
      { header: 'Package Type', key: 'packageType.name' }
    ];

    const headerRow = columns.map(col => col.header).join(',');
    const rows = manifest.packages.map(pkg =>
      columns.map(col => {
        const value = col.key.split('.').reduce((acc, part) => (acc ? acc[part] : ''), pkg);
        const str = value != null ? String(value) : '';
        return `"${str.replace(/"/g, '""')}"`;
      }).join(',')
    );

    const csvContent = [headerRow, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `manifest_${startDate}_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 bg-white shadow-md rounded-lg">
        <h1 className="text-2xl font-bold text-center mb-6">Generate Manifest</h1>
        {/* Date Range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
            <input type="date" id="start-date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">End Date</label>
            <input type="date" id="end-date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
        </div>

        {/* Courier Select */}
        <div className="mb-6">
          <label htmlFor="courier-select" className="block text-sm font-medium text-gray-700">Select Courier</label>
          <select id="courier-select" value={courierId} onChange={e => setCourierId(e.target.value)} className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500">
            {couriers.length > 0 ? couriers.map(c => (
              <option key={c.id} value={c.id}>{c.username} ({c.code})</option>
            )) : <option disabled>No couriers available</option>}
          </select>
        </div>

        {/* Shipment Method */}
        <div className="mb-6">
          <label htmlFor="shipment-method" className="block text-sm font-medium text-gray-700">Select Shipment Method</label>
          <select id="shipment-method" value={shipmentMethod} onChange={e => setShipmentMethod(e.target.value)} className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500">
            {shipmentMethods.length > 0 ? shipmentMethods.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            )) : <option disabled>No shipment methods available</option>}
          </select>
        </div>

        {/* Actions */}
        <div className="flex justify-center space-x-4 mb-4">
          <button onClick={generateManifest} disabled={loading} className="w-1/2 py-3 px-6 bg-blue-800 text-white rounded-md shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:ring-opacity-50">
            {loading ? 'Generating Manifest...' : 'Generate Manifest'}
          </button>
          {manifest && (
            <button onClick={downloadCSV} className="w-1/2 py-3 px-6 bg-green-500 text-white rounded-md shadow-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50">
              Download CSV
            </button>
          )}
        </div>

        {/* Error */}
        {error && <div className="mt-4 text-red-500 text-center">{error}</div>}

        {/* Manifest Details */}
        {manifest && (
          <div className="mt-6 p-4 border-t border-gray-300">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Manifest Summary</h2>
            <div className="mb-8 space-y-2">
              <p className="text-lg font-semibold text-gray-700"><span className="text-gray-600">Total Packages:</span> {manifest.totalPackages}</p>
              <p className="text-lg font-semibold text-gray-700"><span className="text-gray-600">Total Weight:</span> {manifest.totalWeightLb} lbs ({manifest.totalWeightKg} kg)</p>
            </div>
            {manifest.packages && (
              <div className="overflow-x-auto rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AWB</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipper</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Courier</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {manifest.packages.map(pkg => (
                      <tr key={pkg.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{pkg.tracking_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pkg.account.first_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pkg.account.last_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pkg.weight} lbs</td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{pkg.description}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pkg.airWayBill}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{pkg.shipper.username}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">{pkg.deliveryService?.name || "DROP OFF"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ManifestPage;
